
import React, { useState } from 'react';
import CourseCard from './CourseCard';
import { Course, EnrolledCourse } from '@/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuCheckboxItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Search, Filter, SortAsc, SortDesc } from 'lucide-react';

interface CourseCatalogProps {
  courses: Course[];
  enrolledCourses?: EnrolledCourse[];
  onEnroll: (courseId: string) => void;
  onContinueCourse: (courseId: string, moduleId: string) => void;
  className?: string;
}

const CourseCatalog: React.FC<CourseCatalogProps> = ({ 
  courses, 
  enrolledCourses = [],
  onEnroll,
  onContinueCourse,
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevels, setSelectedLevels] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'popular' | 'rating' | 'newest'>('popular');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Get unique categories from courses
  const categories = [...new Set(courses.map(course => course.category))];
  
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        course.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesLevel = selectedLevels.length === 0 || selectedLevels.includes(course.level);
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(course.category);
    
    return matchesSearch && matchesLevel && matchesCategory;
  });
  
  const sortedCourses = [...filteredCourses].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'popular':
        comparison = a.popularity - b.popularity;
        break;
      case 'rating':
        comparison = a.rating - b.rating;
        break;
      case 'newest':
        // For demo, assuming id can represent newness
        comparison = parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1]);
        break;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  
  const toggleLevel = (level: string) => {
    setSelectedLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level) 
        : [...prev, level]
    );
  };
  
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category) 
        : [...prev, category]
    );
  };
  
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  const getEnrolledCourse = (courseId: string) => {
    return enrolledCourses.find(ec => ec.courseId === courseId);
  };
  
  return (
    <div className={className}>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-grow">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search courses..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                <span>Level</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.includes('beginner')}
                onCheckedChange={() => toggleLevel('beginner')}
              >
                Beginner
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.includes('intermediate')}
                onCheckedChange={() => toggleLevel('intermediate')}
              >
                Intermediate
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={selectedLevels.includes('advanced')}
                onCheckedChange={() => toggleLevel('advanced')}
              >
                Advanced
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Filter className="h-4 w-4" />
                <span>Category</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {categories.map(category => (
                <DropdownMenuCheckboxItem
                  key={category}
                  checked={selectedCategories.includes(category)}
                  onCheckedChange={() => toggleCategory(category)}
                >
                  {category}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                {sortOrder === 'asc' ? (
                  <SortAsc className="h-4 w-4" />
                ) : (
                  <SortDesc className="h-4 w-4" />
                )}
                <span>Sort</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuCheckboxItem
                checked={sortBy === 'popular'}
                onCheckedChange={() => sortBy !== 'popular' && setSortBy('popular')}
              >
                Most Popular
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={sortBy === 'rating'}
                onCheckedChange={() => sortBy !== 'rating' && setSortBy('rating')}
              >
                Top Rated
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={sortBy === 'newest'}
                onCheckedChange={() => sortBy !== 'newest' && setSortBy('newest')}
              >
                Newest
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={sortOrder === 'asc'}
                onCheckedChange={toggleSortOrder}
              >
                Ascending Order
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedCourses.map(course => (
          <CourseCard
            key={course.id}
            course={course}
            enrolledCourse={getEnrolledCourse(course.id)}
            onEnroll={onEnroll}
            onContinue={onContinueCourse}
          />
        ))}
        
        {sortedCourses.length === 0 && (
          <div className="col-span-full text-center py-12">
            <p className="text-muted-foreground">No courses match your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseCatalog;
